/**
 * Clipboard handling.
 *
 * A web page can only write the clipboard while its document is focused —
 * Chrome and Edge auto-grant `clipboard-write` to the active tab, so no click
 * is needed once you are looking at it, but a background tab simply cannot.
 * That is a browser rule with no workaround, which is exactly why the optional
 * extension exists (it writes from an offscreen document, no focus required).
 *
 * So the web app's contract is: the newest inbound text is copied the instant
 * you focus the tab. One alt-tab, then Ctrl+V.
 */

export type CopyResult = 'copied' | 'unfocused' | 'denied';

export async function writeClipboard(text: string): Promise<CopyResult> {
  if (!document.hasFocus()) return 'unfocused';
  try {
    await navigator.clipboard.writeText(text);
    return 'copied';
  } catch {
    // Fallback for browsers that still demand a user gesture (Safari, Firefox).
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok ? 'copied' : 'denied';
    } catch {
      return 'denied';
    }
  }
}

/**
 * Echo guard. When we write text that arrived from the phone, the user's own
 * paste-to-send flow must not immediately bounce it back. We remember the last
 * value we wrote and ignore exactly one matching outbound attempt.
 */
export class EchoGuard {
  private lastWritten: string | null = null;

  remember(text: string): void {
    this.lastWritten = text;
  }

  /** True when this outbound text is an echo of what we just received. */
  shouldSuppress(text: string): boolean {
    if (this.lastWritten !== null && this.lastWritten === text) {
      this.lastWritten = null; // one-shot: a deliberate re-send afterwards works
      return true;
    }
    return false;
  }

  clear(): void {
    this.lastWritten = null;
  }
}

const URL_RE = /^https?:\/\/[^\s]+$/i;

export function classifyText(text: string): 'text' | 'link' {
  return URL_RE.test(text.trim()) ? 'link' : 'text';
}
